import { createContext, useContext, useState, ReactNode } from 'react';

export type Currency = 'EUR' | 'USD';

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  formatPrice: (amount: number) => string;
  loading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
];

const STORAGE_KEY = 'airslides_currency';

function detectCountryFromLocale(): string | null {
  try {
    const locale = navigator.language || (navigator as any).userLanguage;
    if (locale) {
      const parts = locale.split('-');
      if (parts.length > 1) {
        return parts[1].toUpperCase();
      }
    }
  } catch {
    // Ignore
  }
  return null;
}

function isEUCountry(countryCode: string | null): boolean {
  if (!countryCode) return false;
  return EU_COUNTRIES.includes(countryCode.toUpperCase());
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<Currency>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as Currency | null;
    if (stored === 'EUR' || stored === 'USD') return stored;
    const country = detectCountryFromLocale();
    const detected: Currency = isEUCountry(country) ? 'EUR' : 'USD';
    localStorage.setItem(STORAGE_KEY, detected);
    return detected;
  });
  const loading = false;

  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    localStorage.setItem(STORAGE_KEY, newCurrency);
  };

  const formatPrice = (amount: number): string => {
    if (currency === 'EUR') {
      return `${amount} €`;
    }
    return `$${amount}`;
  };

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, formatPrice, loading }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
